import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, X } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { fmtGNF } from "@/lib/format";

export const ReturnRequest = ({ order, onDone }) => {
  const [eligibility, setEligibility] = useState(null);
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState("");
  const [goodCondition, setGoodCondition] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (order.status !== "Livrée") return;
    api
      .get(`/orders/${order.id}/return-eligibility`)
      .then((r) => setEligibility(r.data))
      .catch(() => {});
  }, [order.id, order.status]);

  if (!eligibility?.eligible) return null;
  const lines = eligibility.items.filter((i) => i.returnable > 0);
  if (lines.length === 0) return null;

  const total = lines.reduce((s, l) => s + (Number(qty[l.item_id]) || 0) * l.unit_price, 0);

  const submit = async () => {
    const items = lines
      .filter((l) => Number(qty[l.item_id]) > 0)
      .map((l) => ({ item_id: l.item_id, quantity: Number(qty[l.item_id]) }));
    if (items.length === 0) return toast.error("Choisissez au moins un article");
    setBusy(true);
    try {
      const { data } = await api.post("/returns", {
        order_id: order.id,
        items,
        reason,
        good_condition: goodCondition,
      });
      toast.success(`Demande de retour ${data.number} envoyée`);
      window.open(`${API}/returns/${data.id}/label`, "_blank");
      setOpen(false);
      setQty({});
      setReason("");
      setGoodCondition(false);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ds-btn-ghost py-2 text-xs"
        data-testid={`order-return-${order.number}`}
      >
        <RotateCcw className="h-3.5 w-3.5" /> Demander un retour
        {eligibility.hours_left != null && ` (${eligibility.hours_left} h)`}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4" data-testid="return-modal">
          <div className="ds-card w-full max-w-lg p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-bold text-slate-900">Retour — commande {order.number}</h3>
                <p className="mt-1 text-xs text-slate-500">
                  Retour accepté sous {eligibility.window_hours} h après la livraison si l'article est en bon état,
                  complet et dans son emballage.
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400" data-testid="return-modal-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {lines.map((l) => (
                <div key={l.item_id} className="flex items-center gap-3 border-b border-slate-100 pb-2">
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-1 text-sm font-semibold text-slate-900">{l.product_name}</p>
                    <p className="font-num text-xs text-slate-500">
                      {l.returnable} retournable(s) · {fmtGNF(l.unit_price)}
                    </p>
                  </div>
                  <input
                    type="number"
                    min={0}
                    max={l.returnable}
                    value={qty[l.item_id] ?? ""}
                    onChange={(e) => setQty({ ...qty, [l.item_id]: e.target.value })}
                    className="ds-input w-20 text-center"
                    data-testid={`return-qty-${l.sku || l.item_id}`}
                  />
                </div>
              ))}
            </div>

            <label className="ds-label mt-4 block">Motif du retour *</label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="ds-input mt-1"
              placeholder="Ex. taille non adaptée, article non utilisé"
              data-testid="return-reason"
            />

            <label className="mt-3 flex items-start gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={goodCondition}
                onChange={(e) => setGoodCondition(e.target.checked)}
                className="mt-0.5"
                data-testid="return-good-condition"
              />
              Je confirme que l'article est en bon état, complet, non utilisé et dans son emballage d'origine.
            </label>

            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="font-num text-sm font-bold text-brand" data-testid="return-total">{fmtGNF(total)}</p>
              <button
                type="button"
                onClick={submit}
                disabled={busy || !goodCondition || reason.trim().length < 3}
                className="ds-btn-primary"
                data-testid="return-submit"
              >
                Envoyer la demande
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
